import { createApi } from '@reduxjs/toolkit/query/react'
import { API_ENDPOINTS } from 'Constants'
import cloneDeep from 'lodash/cloneDeep'
import { resetUser } from 'features/Authentication'
import { ISpUser } from 'Models/ResponseModels/authentication'
import { bobFoundationUrlQuery, bobPartnerUrlQuery } from 'Services/baseQuery'
import { ToastService } from 'Services/toast.service'
import { IAuthenticatedUser } from 'Models/ResponseModels'

interface ZonedData {
  zone_list: {
    zone_name: string
    zone_target: any[]
  }[]
  consolidated_target: {
    month: string
    year: string
    total_target: number
  }[]
  zone_target: {
    month: string
    year: string
    total_target: number
  }[]
}

interface RegionData {
  zone_target: any[]
  [key: string]: {
    region_name: string
    targets: {
      month: string
      year: string
      total_target: string
    }[]
  }[]
}

interface BranchData {
  region_target: any[]
  [key: string]: {
    sol_id: string
    branch_name: string
    branch_segment: string
    branch_type: string
    targets: {
      month: string
      year: string
      total_target: string
    }[]
  }[]
}
interface IAuthenticatedUserWithToken extends IAuthenticatedUser {
  authtoken: string
}
export const bobFoundationApi = createApi({
  reducerPath: 'bobFoundationApi',
  baseQuery: bobFoundationUrlQuery,
  endpoints: (builder) => ({
    getSpUserDetails: builder.query<ISpUser[], { user_id: string, list_user_of?: string }>({
      query: (params) => ({
        url: API_ENDPOINTS.BOB.GET_SP_USER_DETAILS,
        method: 'GET',
        params,
      }),
    }),

    bobLoginWithToken: builder.query<IAuthenticatedUserWithToken, { token: string }>({
      query: (body) => ({
        url: API_ENDPOINTS.FETCH_USER_DATA,
        method: 'GET',
        headers: { Authorization: `Token ${body.token}` },
      }),
      transformResponse: (response: IAuthenticatedUser, meta, arg) => {
        const updatedResponse = cloneDeep(response)
        updatedResponse.authtoken = arg.token
        return updatedResponse as any
      },
    }),

    // `response_data` decrypts to EITHER the full user payload, or — when the request
    // carried `platform_name` — just `{ otp_reference_id }`. useBankLogin branches on
    // which one it got.
    bobLogin: builder.mutation<{ response_data: string }, { request_data: string }>({
      query: (body) => ({
        url: API_ENDPOINTS.LOGIN,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json', Lang: 'EN' },
      }),
    }),

    bobLoginInfo: builder.mutation<any, any>({
      query: (body) => ({
        url: API_ENDPOINTS.LOGIN_INFO,
        method: 'POST',
        body: {
          journey_platform: 'INTERNET_BANKING',
        },
      }),
    }),

    bobSendOtp: builder.mutation<
      { reference_id: string },
      { partner_emp_id: string; password: string }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.SEND_OTP,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json', Lang: 'EN' },
      }),
    }),

    bobVerifyOtp: builder.mutation<
      { response_data: string },
      { otp_code: string; reference_id: string }
    >({
      query: (body) => ({
        url: API_ENDPOINTS.VERIFY_OTP,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json', Lang: 'EN' },
      }),
    }),

    bobVerifyOtpAssist: builder.mutation<{ response_data: string }, { request_data: string }>({
      query: (body) => ({
        url: API_ENDPOINTS.VERIFY_OTP_ASSIST,
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json', Lang: 'EN' },
      }),
    }),

    bobLogout: builder.mutation<void, null>({
      query: () => ({
        url: API_ENDPOINTS.LOGOUT,
        method: 'POST',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(resetUser())
          ToastService.showSuccessTaost({
            title: 'Success',
            message: 'Logout Successful',
          })
        } catch (e: any) {
          ToastService.showErrorToast({
            title: e?.error?.data?.detail ?? 'Something went wrong',
          })
        }
      },
    }),

    getAllBranchesData: builder.query<
      ZonedData | RegionData | BranchData,
      { zone?: string; region?: string; sol_id?: string }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.GET_ALL_BRANCHES,
        method: 'GET',
        params,
      }),
    }),

    solIdListing: builder.query<any, any>({
      query: (params) => ({
        url: API_ENDPOINTS.GET_SOL_ID_LISTING,
        method: 'GET',
        params,
      }),
    }),
  }),
})

export const {
  useGetSpUserDetailsQuery,
  useBobLoginMutation,
  useBobLoginInfoMutation,
  useLazyBobLoginWithTokenQuery,
  useBobSendOtpMutation,
  useBobVerifyOtpMutation,
  useBobVerifyOtpAssistMutation,
  useBobLogoutMutation,
  useLazyGetAllBranchesDataQuery,
  useLazySolIdListingQuery,
} = bobFoundationApi
